import { useContext, useState } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import { ReactComponent as LoadCircleIcon } from 'assets/load-circle.svg';
import {
  ACCOUNT_PROPERTIES,
  BEFORE_VALIDATE_PAYMENT_METHODS_SORTING_EXAMPLE,
  CURRENCY_SYMBOLS,
  CURRENCY_TYPES,
  CUSTOMER_PERMISSIONS,
  CUSTOMER_TYPE,
  DUKASCOPY_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE,
  GRAPHEREX_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE,
  H_AND_H_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE,
  MANUAL_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE,
  PAYMENT_PROVIDERS
} from 'components/common/constants';
import { BackButton } from 'components/common/BackButton/BackButton';
import { ClipBoard } from 'components/common/ClipBoard/ClipBoard';
import { CurrencyIcon } from 'components/common/CurrencyIcon/CurrencyIcon';
import { sortingPaymentMethods } from 'components/common/PaymentForm/utils';
import { CRYPTO_CURRENCY_PRECISION, CURRENCIES, isStableCoin } from 'services/currenciesConstant';
import { isCryptoProvider, isWallesterProvider, amountFormattedValue } from 'services/utils';
import Button from 'uikit/Button/Button';
import './AccountDetails.scss';
import useCurrencySymbol from 'currency-code-to-symbol';
import ShareAccountModal from '../ShareAccountModal';
import { PopUp } from 'uikit/PopUp/PopUp';

const AccountDetails = ({ accountData, setActiveAccountDetails, userStore }) => {
  const i18n = useContext(i18nContext);
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  // Find the current account from userStore to get reactive updates
  const currentAccount =
    userStore.userAccounts.find((acc) => acc.wallet_number === accountData?.wallet_number) || accountData;

  const isWallesterAccount = isWallesterProvider(currentAccount);
  const hasWalletHolder = !!currentAccount?.wallet_account_holder;
  const isCryptoAccount = isCryptoProvider(currentAccount?.transfer_provider);
  const isShowReference =
    currentAccount?.transfer_provider === PAYMENT_PROVIDERS.MANUAL ||
    currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA ||
    currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT ||
    currentAccount?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API;
  const { symbol } = useCurrencySymbol(currentAccount[ACCOUNT_PROPERTIES.CURRENCY]);
  const isUserHasPermissions = userStore?.hasPermissions;

  const handleCopyAccountDetails = async () => {
    // Get bank details
    const bankDetails = getAccountBankDetailsScheme()
      .filter((rowData) => rowData && rowData.center)
      .map((rowData) => rowData.center);

    // Get recipient details (only for fiat accounts)
    const recipientDetails = !isCryptoAccount
      ? recipientDetailsScheme.filter((rowData) => rowData && rowData.center).map((rowData) => rowData.center)
      : [];

    // Combine all details
    const allDetails = [...bankDetails, ...recipientDetails];

    // Copy to clipboard with each detail on a new line
    const textToCopy = allDetails.join('\n');
    await navigator.clipboard.writeText(textToCopy);

    // Show copied notification
    setShowCopiedNotification(true);
    setTimeout(() => {
      setShowCopiedNotification(false);
    }, 2000);
  };

  const handleDownloadAccountDetailsPdf = async () => {
    await userStore.downloadAccountDetailsPdf(
      userStore?.userData?.account?.account_number,
      currentAccount?.wallet_number
    );
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    userStore.clearAccountDetailsError();
  };

  const handleShareAccount = async (customerId) => {
    try {
      setIsSharing(true);
      await userStore.shareWallet(
        userStore?.userData?.account?.account_number,
        currentAccount?.wallet_number,
        customerId
      );
      setShowShareModal(false);
      // Optionally refresh account data
    } catch (err) {
      // Error is handled in the store
    } finally {
      setIsSharing(false);
    }
  };

  const handleRemoveSharing = async () => {
    try {
      setIsSharing(true);
      await userStore.unshareWallet(userStore?.userData?.account?.account_number, currentAccount?.wallet_number);
      // Optionally refresh account data
    } catch (err) {
      // Error is handled in the store
    } finally {
      setIsSharing(false);
    }
  };

  const dataRowContentScheme = (left, center) => {
    return (
      <div key={left} className={'account-details-data-row'}>
        <p className={'account-details-data-row-label'}>{left}</p>
        <div className={'account-details-data-row-data-wrapper'}>
          <p className={'account-details-data-row-data'}>{center}</p>
          <ClipBoard copyText={center} />
        </div>
      </div>
    );
  };

  const getPaymentMethods = () => {
    const sortedMethods = sortingPaymentMethods(
      accountData?.payment_methods,
      accountData?.currency,
      accountData?.transfer_provider === PAYMENT_PROVIDERS.MANUAL
        ? MANUAL_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
        : accountData?.transfer_provider === PAYMENT_PROVIDERS.GRAPHEREX
          ? GRAPHEREX_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
          : accountData?.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY
            ? DUKASCOPY_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
            : accountData?.transfer_provider === PAYMENT_PROVIDERS.H_AND_H
              ? H_AND_H_PROVIDER_PAYMENT_METHODS_SORTING_EXAMPLE
              : BEFORE_VALIDATE_PAYMENT_METHODS_SORTING_EXAMPLE,
      undefined,
      accountData?.transfer_provider
    );

    return sortedMethods.map((method) => i18n.getMessage(`transfer.paymentMethod.${method}`)).join(', ');
  };

  const fiatAccountBankDetailsScheme = [
    accountData?.iban?.iban || accountData?.multi_iban?.iban
      ? {
          left: i18n.getMessage('accountDetails.left.IBAN'),
          center: accountData?.iban?.iban || accountData?.multi_iban?.iban
        }
      : null,
    (accountData?.transfer_provider === PAYMENT_PROVIDERS.H_AND_H && accountData?.wallet_account_number) ||
    (!accountData?.iban?.iban && !accountData?.multi_iban?.iban && accountData?.wallet_account_number)
      ? {
          left: i18n.getMessage('accountDetails.left.accountNumber'),
          center: accountData?.wallet_account_number
        }
      : accountData.currency === CURRENCY_TYPES.GBP &&
          (accountData?.iban?.account_number || accountData.multi_iban?.account_number)
        ? {
            left: i18n.getMessage('accountDetails.left.accountNumber'),
            center: accountData?.iban?.account_number || accountData.multi_iban?.account_number
          }
        : accountData?.transfer_provider === PAYMENT_PROVIDERS.MANUAL ||
            accountData?.transfer_provider === PAYMENT_PROVIDERS.WALLESTER ||
            accountData?.transfer_provider === PAYMENT_PROVIDERS.DUKASCOPY
          ? {
              left: i18n.getMessage('accountDetails.left.accountNumber'),
              center: accountData?.iban?.account_number || accountData?.wallet_number
            }
          : null,
    accountData?.currency === CURRENCY_TYPES.GBP && (accountData?.iban?.sort_code || accountData?.multi_iban?.sort_code)
      ? {
          left: i18n.getMessage('accountDetails.left.sortCode'),
          center: accountData?.iban?.sort_code || accountData?.multi_iban?.sort_code
        }
      : null,
    accountData.bic
      ? {
          left: i18n.getMessage('accountDetails.left.BIC'),
          center: accountData.bic
        }
      : null,
    accountData.account_holder
      ? {
          left: i18n.getMessage('accountDetails.left.bankAccountHolderName'),
          center: accountData.account_holder
        }
      : null,
    accountData.bank_name
      ? {
          left: i18n.getMessage('accountDetails.left.bankName'),
          center: accountData.bank_name
        }
      : null,
    accountData.bank_address
      ? {
          left: i18n.getMessage('accountDetails.left.bankAddress'),
          center: accountData.bank_address
        }
      : null,
    accountData.bank_country
      ? {
          left: i18n.getMessage('accountDetails.left.bankCountry'),
          center: i18n.getMessage(`country.${accountData.bank_country}`)
        }
      : null,
    {
      left: i18n.getMessage('accountDetails.left.paymentMethods'),
      center: getPaymentMethods()
    }
  ];

  const cryptoAccountBankDetailsScheme = [
    {
      left: i18n.getMessage('accountDetails.left.cryptoAddress'),
      center: accountData?.crypto_address
    },
    {
      left: i18n.getMessage('accountDetails.left.networkName'),
      center: accountData?.network_name
    },
    {
      left: i18n.getMessage('accountDetails.left.paymentMethods'),
      center: getPaymentMethods()
    }
  ];

  const recipientDetailsScheme = [
    {
      left: i18n.getMessage('accountDetails.left.recipient'),
      center: userStore?.userData?.account?.common_name
    },
    userStore?.userData?.account?.type === CUSTOMER_TYPE.COMPANY
      ? {
          left: i18n.getMessage('accountDetails.left.recipientAddress'),
          center: userStore?.userData?.account?.details?.legal_address?.address_1
        }
      : {
          left: i18n.getMessage('accountDetails.left.recipientAddress'),
          center: userStore?.userData?.account?.details?.address?.address_1
        },
    userStore?.userData?.account?.type === CUSTOMER_TYPE.COMPANY
      ? {
          left: i18n.getMessage('accountDetails.left.recipientCountry'),
          center: i18n.getMessage(`country.${userStore?.userData?.account?.details?.legal_address?.country}`)
        }
      : {
          left: i18n.getMessage('accountDetails.left.recipientCountry'),
          center: i18n.getMessage(`country.${userStore?.userData?.account?.details?.address?.country}`)
        },
    isShowReference && accountData?.transfer_provider === PAYMENT_PROVIDERS.CONVERA
      ? {
          left: i18n.getMessage('accountDetails.left.reference'),
          center: `${process.env.REACT_APP_CONVERA_PROVIDER_REFERENCE}, ${accountData.wallet_number}, ${i18n.getMessage(
            'accountDetails.right.referenceEnd'
          )}`
        }
      : isShowReference && accountData?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_MERCHANT
        ? {
            left: i18n.getMessage('accountDetails.left.reference'),
            center: `${process.env.REACT_APP_CONVERA_MERCHANT_PROVIDER_REFERENCE}, ${accountData.wallet_number}, ${i18n.getMessage(
              'accountDetails.right.referenceEnd'
            )}`
          }
        : isShowReference && accountData?.transfer_provider === PAYMENT_PROVIDERS.CONVERA_API
          ? {
              left: i18n.getMessage('accountDetails.left.reference'),
              center: `${process.env.REACT_APP_CONVERA_PROVIDER_REFERENCE}, ${accountData.wallet_number} (${i18n.getMessage(
                'accountDetails.right.referenceEndForConveraApi'
              )})`
            }
          : isShowReference
            ? {
                left: i18n.getMessage('accountDetails.left.reference'),
                center: `${accountData.wallet_number}, ${i18n.getMessage('accountDetails.right.referenceEnd')}`
              }
            : null
  ];

  const getAccountBankDetailsScheme = () => {
    if (isCryptoAccount) {
      return cryptoAccountBankDetailsScheme;
    } else {
      return fiatAccountBankDetailsScheme;
    }
  };

  return (
    <>
      <div className={'section-right account-details-main-container'}>
        <div className={'account-details-wrapper'}>
          <BackButton handleBackButton={() => setActiveAccountDetails(null)} withoutBottomMargin={true} />
          <div className={'account-details-main-form-wrapper'}>
            <div className={'account-details-currency-wrapper'}>
              <div className={'account-details-currency-code-wrapper'}>
                <CurrencyIcon
                  isCardAccount={isWallesterAccount}
                  currencyIcon={
                    isCryptoAccount && isStableCoin(accountData[ACCOUNT_PROPERTIES.CURRENCY])
                      ? CURRENCIES[accountData[ACCOUNT_PROPERTIES.CURRENCY]]?.networks?.[
                          accountData[ACCOUNT_PROPERTIES.NETWORK]
                        ]?.icon || null
                      : CURRENCIES[accountData[ACCOUNT_PROPERTIES.CURRENCY]]?.icon || CURRENCIES.OTHERS?.icon || null
                  }
                />
                <p
                  className={`${isCryptoAccount ? 'account-details-crypto-currency-code' : 'account-details-currency-code'}`}
                >
                  {accountData[ACCOUNT_PROPERTIES.CURRENCY]}
                </p>
                {isCryptoAccount &&
                  isStableCoin(accountData[ACCOUNT_PROPERTIES.CURRENCY]) &&
                  accountData[ACCOUNT_PROPERTIES.NETWORK] && (
                    <p className={'account-details-currency-code'}>{accountData[ACCOUNT_PROPERTIES.NETWORK]}</p>
                  )}
              </div>

              {/* Shared Account Badge */}
              {isWallesterAccount && hasWalletHolder && (
                <div className={'account-details-shared-badge-container'}>
                  <div className={'account-details-shared-badge'}>
                    <span className={'badge-text'}>{i18n.getMessage('accountDetails.badge.sharedAccount')}</span>
                  </div>
                </div>
              )}
            </div>

            <div className={'account-details-balance-wrapper'}>
              <div className={'account-details-balance-or-id-column-wrapper'}>
                <p className={'account-details-balance-label'}>{i18n.getMessage('accounts.card.available')}:</p>
                <p className={'account-details-balance'}>
                  {isCryptoAccount
                    ? `${amountFormattedValue(
                        accountData[ACCOUNT_PROPERTIES.AVAILABLE],
                        CRYPTO_CURRENCY_PRECISION
                      )} ${accountData[ACCOUNT_PROPERTIES.CURRENCY]}`
                    : `${symbol || CURRENCY_SYMBOLS[accountData[ACCOUNT_PROPERTIES.CURRENCY]] || accountData[ACCOUNT_PROPERTIES.CURRENCY]}${amountFormattedValue(
                        accountData[ACCOUNT_PROPERTIES.AVAILABLE],
                        2
                      )}`}
                </p>
              </div>
              <div className={'account-details-balance-or-id-row-wrapper'}>
                <p className={'account-details-balance-label'}>{i18n.getMessage('accounts.card.pending')}:</p>
                <p className={'account-details-balance-label'}>
                  {isCryptoAccount
                    ? `${amountFormattedValue(
                        accountData[ACCOUNT_PROPERTIES.ON_HOLD],
                        CRYPTO_CURRENCY_PRECISION
                      )} ${accountData[ACCOUNT_PROPERTIES.CURRENCY]}`
                    : `${symbol || CURRENCY_SYMBOLS[accountData[ACCOUNT_PROPERTIES.CURRENCY]] || accountData[ACCOUNT_PROPERTIES.CURRENCY]}${amountFormattedValue(
                        accountData[ACCOUNT_PROPERTIES.ON_HOLD],
                        2
                      )}`}
                </p>
              </div>
            </div>

            <div className={'account-details-balance-or-id-row-wrapper'}>
              <p className={'account-details-currency-code'}>{i18n.getMessage('accountDetails.left.walletId')}</p>
              <p className={'account-details-currency-code'}>{accountData.wallet_number}</p>
            </div>

            <div className={`account-details-data-wrapper ${!isCryptoAccount ? 'bank-details-wrapper' : ''}`}>
              <p className={'account-details-data-header'}>{i18n.getMessage('accountDetails.header.bankDetails')}</p>
              {getAccountBankDetailsScheme()?.map(
                (rowData) => rowData && dataRowContentScheme(rowData.left, rowData.center)
              )}
            </div>

            {!isCryptoAccount && (
              <div className={'account-details-data-wrapper'}>
                <p className={'account-details-data-header'}>
                  {i18n.getMessage('accountDetails.header.recipientDetails')}
                </p>
                {recipientDetailsScheme.map((rowData) => rowData && dataRowContentScheme(rowData.left, rowData.center))}
              </div>
            )}

            {isCryptoAccount && (
              <div className={'account-details-crypto-account-qr-code-wrapper'}>
                <div className={'account-details-crypto-account-qr-code'}>
                  <QRCodeSVG value={accountData?.crypto_address} size={160} minVersion={10} />,
                </div>
                <div className={'account-details-crypto-account-qr-code-description-wrapper'}>
                  <p className={'account-details-crypto-account-qr-code-description'}>
                    {i18n.getMessage('accountDetails.qrCode.description')}
                  </p>
                  <p className={'account-details-crypto-account-qr-code-description'}>
                    {i18n.getMessage('accountDetails.qrCode.instructions', {
                      paymentInstitution: process.env.REACT_APP_PAYMENT_INSTITUTION
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>

          {!isCryptoAccount && (
            <div className={'account-details-actions-wrapper'}>
              <div className={'account-details-copy-button-wrapper'}>
                <Button
                  className={'account-details-actions-button'}
                  size={'large'}
                  type={'outline'}
                  roleType={'button'}
                  onClick={handleCopyAccountDetails}
                  isDisabled={userStore.isLoadingAccountDetails}
                >
                  {i18n.getMessage('accountDetails.button.copyDetails')}
                </Button>
                {showCopiedNotification && (
                  <div className={'account-details-copied-notification'}>
                    <p>{i18n.getMessage('accountDetails.tooltip.copied')}</p>
                  </div>
                )}
              </div>

              {/* Wallester Account Actions */}
              {isWallesterAccount ? (
                hasWalletHolder ? (
                  <Button
                    className={'account-details-actions-button'}
                    size={'large'}
                    type={'outline'}
                    onClick={handleRemoveSharing}
                    isDisabled={isSharing || userStore.isLoading || userStore.isLoadingAccountDetails}
                  >
                    {i18n.getMessage('accountDetails.button.removeSharing')}
                  </Button>
                ) : (
                  <Button
                    className={'account-details-actions-button'}
                    size={'large'}
                    type={'outline'}
                    onClick={() => setShowShareModal(true)}
                    isDisabled={userStore.isLoading || userStore.isLoadingAccountDetails}
                  >
                    {i18n.getMessage('accountDetails.button.shareAccount')}
                  </Button>
                )
              ) : (
                <Button
                  className={'account-details-actions-button'}
                  size={'large'}
                  onClick={handleDownloadAccountDetailsPdf}
                  isDisabled={
                    !isUserHasPermissions([CUSTOMER_PERMISSIONS.CREATE_REPORT]) || userStore.isLoadingAccountDetails
                  }
                >
                  <LoadCircleIcon className={'account-details-actions-button-icon'} />
                  {i18n.getMessage('accountDetails.button.downloadPdf')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Share Account Modal */}
      <PopUp show={showShareModal} onClose={handleCloseShareModal} alignOnCenter={true} className='share-account-popup'>
        <ShareAccountModal
          isLoading={isSharing}
          onClose={handleCloseShareModal}
          onConfirm={handleShareAccount}
          error={userStore.errorAccountDetails?.message}
          clearError={() => userStore.clearAccountDetailsError()}
        />
      </PopUp>
    </>
  );
};

AccountDetails.propTypes = {
  accountData: PropTypes.object,
  setActiveAccountDetails: PropTypes.func,
  userStore: MobXPropTypes.observableObject
};

export default inject('userStore')(observer(AccountDetails));
